import { StatusType, AnyUserData } from 'types'
import { ErrorProp, authorizedRequest } from './'
import { STUDENT_DC, STUDENT_INPERSON, TEACHER_DC } from '../constants'
import { ListTypes } from 'pages/Dashboard'

export type ListResponseProps =
  | {
      success: true
      total: number
      results: Partial<AnyUserData>[]
    }
  | {
      success: false
      error: ErrorProp
      message?: string
    }

export type ListProps = {
  filters?: {
    status: string | undefined
    progress: string | undefined
    userType: string | undefined
  }
  size?: number
  sort?: string
  order?: string
  from?: number
  teacherId?: string
  type?: ListTypes | string
}
type CourseType = 'TMATT' | 'DC,InPerson' | 'DC' | 'InPerson' | 'TMATTrefresher'
export type QueryProps = {
  size: string
  sort: string
  order: string
  from: string
  courseType: CourseType
  status?: string | undefined
  id?: string
}
const possibleStatus = {
  TMATT: {
    done: 'completed',
    'not-done':
      'pre-course,signature-pending,progress,waiting,expired,deactivated',
    'done,not-done':
      'completed,pre-course,signature-pending,progress,waiting,expired,deactivated',
  },
  TMATTrefresher: {
    done: 'completed',
    'not-done': 'pre-course,progress,waiting,expired,deactivated',
    'done,not-done':
      'completed,pre-course,progress,waiting,expired,deactivated',
  },
  'DC,InPerson': {
    done: 'completed,completed-review,review',
    'not-done':
      'unlocked,progress,deactivated,waiting,invited,incomplete,locked',
    'done,not-done':
      'locked,unlocked,progress,review,deactivated,completed,waiting,invited,incomplete,completed-review',
  },
  DC: {
    done: 'completed,completed-review,review',
    'not-done': 'unlocked,progress,deactivated,incomplete,locked',
    'done,not-done':
      'locked,unlocked,progress,review,deactivated,completed,completed-review,incomplete',
  },
  InPerson: {
    done: 'completed',
    'not-done': 'waiting,invited',
    'done,not-done': 'completed,waiting,invited',
  },
}
type AdjustPossibleFiltersOutput = { possibleFilteredStatus: string }

const adjustPossibleFilters = (
  {
    userType,
    progress,
    status,
  }: {
    userType: string | undefined
    progress: string | undefined
    status: string | undefined
  },
  courseType: CourseType
): AdjustPossibleFiltersOutput => {
  if (progress === undefined && status === undefined) {
    return {
      possibleFilteredStatus: possibleStatus[courseType]['done,not-done'],
    }
  }
  const progressStatus = possibleStatus[courseType]
  const finalStatus = progressStatus[progress as keyof typeof progressStatus]
  return {
    possibleFilteredStatus: finalStatus.toString(),
  }
}
export async function list({
  filters,
  teacherId,
  size = 7,
  sort = 'instructionDate',
  order = 'asc',
  from = 0,
  type = 'student',
}: ListProps): Promise<ListResponseProps> {
  const courseType =
    type === 'tmatt'
      ? 'TMATT'
      : type === 'refresher'
      ? 'TMATTrefresher'
      : 'DC,InPerson'
  let defaultStatus: Partial<StatusType>[] =
    courseType === 'DC,InPerson'
      ? [
          'unlocked',
          'progress',
          'review',
          'deactivated',
          'completed',
          'waiting',
          'invited',
          'incomplete',
          'completed-review',
          'locked',
        ]
      : courseType === 'TMATT'
      ? [
          'completed',
          'pre-course',
          'signature-pending',
          'progress',
          'waiting',
          'expired',
          'deactivated',
        ]
      : [
          'completed',
          'pre-course',
          'progress',
          'waiting',
          'expired',
          'deactivated',
        ]
  const params: QueryProps = {
    size: size.toString(),
    sort: sort.toString(),
    order: order.toString(),
    from: from.toString(),
    status: defaultStatus.toString(),
    courseType,
  }
  if (filters && filters.progress && filters.status) {
    filters?.userType === STUDENT_DC && (params.courseType = 'DC')
    filters?.userType === STUDENT_INPERSON && (params.courseType = 'InPerson')
    const { possibleFilteredStatus } = adjustPossibleFilters(
      filters,
      params.courseType
    )
    const possibleFilteredStatusArray = possibleFilteredStatus.split(',')
    const incomeStatusArray = filters.status?.split(',') || []
    if (incomeStatusArray.length === 0) {
      params.status = possibleFilteredStatusArray.toString()
    }
    const statusArray = incomeStatusArray
      .filter((status) => possibleFilteredStatusArray.includes(status))
      .toString()
    if (statusArray.length !== 0) {
      params.status = possibleFilteredStatusArray
        .filter((status) => incomeStatusArray.includes(status))
        .toString()
    } else {
      params.status = ''
    }
  }
  if (teacherId) {
    params.id = teacherId
  }

  const endpoint = `list/${type}`
  try {
    const response = await authorizedRequest({
      endpoint,
      method: 'GET',
      params,
    })
    const json = (await response.json()) as ListResponseProps

    if (json.success !== false) {
      const { total, results } = json
      let processedResults = results?.map((user) => {
        if (user.status) {
          return user
        } else if (user.userType === TEACHER_DC) {
          return { ...user, status: 'teacher' as StatusType }
        } else {
          return { ...user, status: 'unknown' as StatusType }
        }
      })
      return {
        success: true,
        results: processedResults,
        total,
      }
    } else {
      return {
        success: false,
        error: json.error ?? json.message,
      }
    }
  } catch (e: any) {
    throw e
  }
}
